import { Breadcrumb, Tabs, Typography } from "antd";
import { Link, useLoaderData, useSubmit } from "react-router-dom";
import { HomeOutlined } from "@ant-design/icons";
import { AmenityDetailsPageLoaderType } from "./loaders";
import { AmenityAvailabilityTab, AmenityDetailsTab } from "./components";
import { AmenityReservationTab } from "./AmenityDetailsReservation";
import {
  RecurringScheduleItems,
  ScheduleItem,
  ScheduleItemList,
} from "~/types";
import { CustomTextField } from "~/components";
import { COLORS, ROUTES, sparseTimeFrameFormat } from "~/utils";
import * as APITypes from "~/API";
import { useAppTranslation } from "~/hooks/useAppTranslation";

export const AmenityDetailsPage = () => {
  const loaderData = useLoaderData() as AmenityDetailsPageLoaderType;
  const { t } = useAppTranslation();

  const amenityDetails =
    loaderData.amenityData.formattedAmenityDetails.amenityDetails;

  const submit = useSubmit();

  const handleSubmit = (value: string) => {
    const values = { tab: value } as Record<string, string>;
    submit(values, { method: "get" });
  };

  const amenityData = loaderData.amenityData;
  const availability = amenityData.availability;

  const scheduleItems: ScheduleItemList[] = [
    {
      scheduleItems: availability.unavailable.map((unavail): ScheduleItem => {
        return {
          timeFrame: unavail.timeFrame,
          popOver: (
            <>
              <Typography.Text strong>{t("Reason")}</Typography.Text>
              <br />
              <CustomTextField value={unavail.reason} onEmpty="none" />
              <Typography.Text strong>{t("Time_Frame")}</Typography.Text>
              <br />
              {sparseTimeFrameFormat(unavail.timeFrame)}
            </>
          ),
        };
      }),
      color: COLORS.dark_red,
      description: t("Unavailable"),
    },
    {
      scheduleItems: amenityData.formattedAmenityDetails.reservations
        .filter(
          (value) =>
            value.data.status === APITypes.AmenityReservationStatus.Approved
        )
        .map((reservation): ScheduleItem => {
          return {
            timeFrame: reservation.timeFrame,
            popOver: (
              <>
                <Typography.Text strong>{t("Reason")}</Typography.Text>
                <br />
                <Typography.Text>{t("Reserved")}</Typography.Text>
                <br />
                <Typography.Text strong>{t("Time_Frame")}</Typography.Text>
                <br />
                {sparseTimeFrameFormat(reservation.timeFrame)}
              </>
            ),
          };
        }),
      color: COLORS.dim_gray,
      description: t("Reserved"),
    },
  ];

  const recurringScheduleItems: RecurringScheduleItems[] = [
    {
      weeklyTimes: availability.available,
      color: COLORS.lime,
      description: t("Available"),
    },
  ];

  return (
    <>
      <Breadcrumb separator=">">
        <Breadcrumb.Item href={ROUTES.home}>
          <HomeOutlined />
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <Link to={ROUTES.amenity}>{t("Amenities")}</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <>
            <b>{amenityDetails.name}</b>
          </>
        </Breadcrumb.Item>
      </Breadcrumb>
      <Typography.Title level={1}>
        {t("Amenity")}: {amenityDetails.name}
      </Typography.Title>

      <Tabs
        defaultActiveKey={loaderData.tab}
        onChange={(to) => {
          handleSubmit(to);
        }}
        items={[
          {
            label: t("Details"),
            key: "details",
            children: (
              <AmenityDetailsTab
                amenityDetails={amenityDetails}
                fileList={loaderData.fileList.data}
                buildingName={loaderData.buildingName}
              />
            ),
          },
          {
            label: t("Availability"),
            key: "availability",
            children: (
              <AmenityAvailabilityTab
                amenityData={loaderData.amenityData}
                scheduleItems={scheduleItems}
                recurringScheduleItems={recurringScheduleItems}
              />
            ),
          },
          {
            label: t("Reservation"),
            key: "reservations",
            children: (
              <AmenityReservationTab
                amenityData={loaderData.amenityData}
                scheduleItems={scheduleItems}
                recurringScheduleItems={recurringScheduleItems}
                filters={loaderData.filters}
                tenantID={loaderData.userID}
              />
            ),
          },
        ]}
      />
    </>
  );
};
